import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../api/axios';
import { useRiskProcessFilterOptions } from '../../hooks/risks/useRisksTableList.ts';
import { risksQueryKeys } from '../../queries/risks/keys.ts';

/**
 * RCM list filter state, debounced search, and async process/control option lists.
 * Page, page size, and sort stay in `RiskControlMatrix` (or parent).
 */
export function useRcmListFilters() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterProcess, setFilterProcess] = useState('all');
  const [filterControlId, setFilterControlId] = useState('all');
  const [filterFrequency, setFilterFrequency] = useState('all');
  const [filterControlType, setFilterControlType] = useState('all');
  const [filterSignificance, setFilterSignificance] = useState('all');
  const [filterRiskCategory, setFilterRiskCategory] = useState('all');
  const [processFilterSearchInput, setProcessFilterSearchInput] = useState('');
  const [processFilterSearch, setProcessFilterSearch] = useState('');
  const [controlFilterSearchInput, setControlFilterSearchInput] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const nextSearch = processFilterSearchInput.trim();
    if (nextSearch === '') {
      setProcessFilterSearch('');
      return;
    }
    const t = window.setTimeout(() => setProcessFilterSearch(nextSearch), 400);
    return () => window.clearTimeout(t);
  }, [processFilterSearchInput]);

  const {
    data: processFilterPages,
    isFetching: loadingProcessFilterOptions,
    isFetchingNextPage: loadingMoreProcessFilterOptions,
    hasNextPage: hasMoreProcessFilterOptions,
    fetchNextPage: fetchMoreProcessFilterOptions,
  } = useRiskProcessFilterOptions(processFilterSearch);

  const processFilterOptions = useMemo(() => {
    const items = (processFilterPages?.pages ?? []).flatMap((p) => p.items);
    return [{ value: 'all', label: 'All Processes' }, ...items];
  }, [processFilterPages]);

  const processFilterNoOptionsMessage = useMemo(
    () => () => (loadingProcessFilterOptions ? 'Loading processes...' : 'No processes found'),
    [loadingProcessFilterOptions]
  );

  const { data: existingControls = [] } = useQuery({
    queryKey: risksQueryKeys.controlFilterOptions('rcm-matrix'),
    queryFn: async () => {
      const response = await axiosInstance.get('/api/sox-controls');
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const controlFilterOptions = useMemo(() => {
    const search = controlFilterSearchInput.trim().toLowerCase();
    const mapped = existingControls
      .map((controlRow) => {
        const uuid = String(controlRow?.id ?? '').trim();
        if (!uuid) return null;
        const displayId = String(controlRow?.control_id ?? controlRow?.control_display_id ?? '').trim();
        const title = String(controlRow?.title ?? '').trim();
        const labelLead = displayId || uuid;
        const label = title ? `${labelLead} - ${title}` : labelLead;
        return { value: uuid, label };
      })
      .filter(Boolean);
    const filtered = search
      ? mapped.filter((opt) => opt.label.toLowerCase().includes(search))
      : mapped;
    return [{ value: 'all', label: 'All Controls' }, ...filtered];
  }, [existingControls, controlFilterSearchInput]);

  const controlFilterNoOptionsMessage = useMemo(
    () => () => (controlFilterOptions.length <= 1 ? 'No controls found' : null),
    [controlFilterOptions.length]
  );

  const hasActiveListCriteria = useMemo(
    () =>
      debouncedSearchQuery.trim() !== '' ||
      filterProcess !== 'all' ||
      filterControlId !== 'all' ||
      filterFrequency !== 'all' ||
      filterControlType !== 'all' ||
      filterSignificance !== 'all' ||
      filterRiskCategory !== 'all',
    [
      debouncedSearchQuery,
      filterProcess,
      filterControlId,
      filterFrequency,
      filterControlType,
      filterSignificance,
      filterRiskCategory,
    ]
  );

  const apiFilterParams = useMemo(
    () => ({
      search: debouncedSearchQuery,
      processId: filterProcess,
      controlId: filterControlId,
      frequency: filterFrequency,
      controlType: filterControlType,
      significance: filterSignificance,
      riskCategory: filterRiskCategory,
    }),
    [
      debouncedSearchQuery,
      filterProcess,
      filterControlId,
      filterFrequency,
      filterControlType,
      filterSignificance,
      filterRiskCategory,
    ]
  );

  const handleProcessFilterChange = useCallback((value) => {
    setFilterProcess(typeof value === 'string' ? value : 'all');
  }, []);

  const handleProcessFilterInputChange = useCallback((value, meta) => {
    if (meta.action === 'input-change') setProcessFilterSearchInput(value);
  }, []);

  const handleProcessFilterMenuClose = useCallback(() => {
    setProcessFilterSearchInput('');
  }, []);

  const handleProcessFilterMenuScrollToBottom = useCallback(() => {
    if (hasMoreProcessFilterOptions && !loadingMoreProcessFilterOptions) {
      void fetchMoreProcessFilterOptions();
    }
  }, [fetchMoreProcessFilterOptions, hasMoreProcessFilterOptions, loadingMoreProcessFilterOptions]);

  const handleControlFilterChange = useCallback((value) => {
    setFilterControlId(typeof value === 'string' ? value : 'all');
  }, []);

  const handleControlFilterInputChange = useCallback((value, meta) => {
    if (meta.action === 'input-change') setControlFilterSearchInput(value);
  }, []);

  const handleControlFilterMenuClose = useCallback(() => {
    setControlFilterSearchInput('');
  }, []);

  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setFilterProcess('all');
    setFilterControlId('all');
    setFilterFrequency('all');
    setFilterControlType('all');
    setFilterSignificance('all');
    setFilterRiskCategory('all');
    setProcessFilterSearchInput('');
    setProcessFilterSearch('');
    setControlFilterSearchInput('');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filterControlType,
    setFilterControlType,
    filterSignificance,
    setFilterSignificance,
    filterFrequency,
    setFilterFrequency,
    filterRiskCategory,
    setFilterRiskCategory,
    filterProcess,
    filterControlId,
    processFilterOptions,
    controlFilterOptions,
    processFilterNoOptionsMessage,
    controlFilterNoOptionsMessage,
    loadingMoreProcessFilterOptions,
    handleProcessFilterChange,
    handleProcessFilterInputChange,
    handleProcessFilterMenuClose,
    handleProcessFilterMenuScrollToBottom,
    handleControlFilterChange,
    handleControlFilterInputChange,
    handleControlFilterMenuClose,
    resetFilters,
    apiFilterParams,
    hasActiveListCriteria,
  };
}
